"use client";

import { useEffect, useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, Star } from "lucide-react";
import { iHolidayQuoteItineraryBlock } from "@/@sdk/holiday-quotes-x/holiday-quotes-x-model";
import { GlobalConfig, resolveEngineAssetUrl } from "@/config";
import {
  quoteThemeColorStyles,
  quoteThemeCssVars,
} from "../quote-theme/quote-theme-css-vars";
import { cn } from "@/lib/utils";

interface HotelStayCardProps {
  hotel: iHolidayQuoteItineraryBlock;
  onView: () => void;
}

function plainText(html: string) {
  return html.replace(/<[^>]*>/g, " ").replace(/\s+/g, " ").trim();
}

function collectImages(hotel: iHolidayQuoteItineraryBlock): string[] {
  const data = hotel.accommodationData;
  if (!data) return [];

  if (data.images?.length) {
    return data.images.map(resolveEngineAssetUrl).filter(Boolean);
  }

  return (data.place?.photos || [])
    .filter((item: { width: number; height: number }) => item.width > item.height)
    .map(
      (record: { photo_reference: string }) =>
        `${GlobalConfig.ENGINE_BASE_PATH}/location/photos?photoReference=${record.photo_reference}`
    )
    .slice(0, 8);
}

function starCount(hotel: iHolidayQuoteItineraryBlock): number {
  const rating = hotel.accommodationData?.place?.rating;
  if (typeof rating !== "number" || Number.isNaN(rating)) return 0;
  return Math.max(0, Math.min(5, Math.round(rating)));
}

export function HotelStayCard({ hotel, onView }: HotelStayCardProps) {
  const data = hotel.accommodationData;
  const name = data?.accommodationName || data?.title || "Hotel";
  const overview = plainText(data?.overview || "");
  const images = useMemo(() => collectImages(hotel), [hotel]);
  const stars = starCount(hotel);
  const [index, setIndex] = useState(0);

  useEffect(() => {
    if (index > images.length - 1) {
      setIndex(Math.max(0, images.length - 1));
    }
  }, [images.length, index]);

  const safeIndex = images.length ? Math.min(index, images.length - 1) : 0;
  const preview =
    overview.length > 140 ? `${overview.slice(0, 140).trim()}…` : overview;

  return (
    <article
      className="flex h-full flex-col overflow-hidden rounded-2xl border bg-white shadow-sm"
      style={{ borderColor: quoteThemeCssVars.primary20 }}
    >
      <div className="relative aspect-[16/10] bg-gray-100">
        {images.length > 0 ? (
          <img
            src={images[safeIndex]}
            alt=""
            className="absolute inset-0 h-full w-full object-cover"
          />
        ) : (
          <div className="absolute inset-0 flex items-center justify-center text-sm text-gray-400">
            No photos
          </div>
        )}

        {images.length > 1 ? (
          <div className="absolute inset-x-0 bottom-0 flex items-center gap-2 bg-black/45 px-2 py-2">
            <button
              type="button"
              aria-label="Previous photo"
              onClick={() =>
                setIndex((prev) =>
                  prev === 0 ? images.length - 1 : prev - 1
                )
              }
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-white/80 text-gray-800"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <div className="flex min-w-0 flex-1 gap-1.5 overflow-x-auto">
              {images.slice(0, 6).map((src, i) => (
                <button
                  key={`${src}-${i}`}
                  type="button"
                  onClick={() => setIndex(i)}
                  className={cn(
                    "h-9 w-9 shrink-0 overflow-hidden rounded-md border-2",
                    i === safeIndex ? "border-white" : "border-transparent opacity-80"
                  )}
                >
                  <img src={src} alt="" className="h-full w-full object-cover" />
                </button>
              ))}
            </div>
            <button
              type="button"
              aria-label="Next photo"
              onClick={() =>
                setIndex((prev) =>
                  prev === images.length - 1 ? 0 : prev + 1
                )
              }
              className="flex h-8 w-8 shrink-0 items-center justify-center rounded-md bg-white/80 text-gray-800"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        ) : null}
      </div>

      <div className="flex flex-1 flex-col gap-3 p-4 sm:p-5">
        {stars > 0 ? (
          <div className="flex items-center gap-0.5" aria-label={`${stars} stars`}>
            {Array.from({ length: 5 }).map((_, i) => (
              <Star
                key={i}
                className={cn(
                  "h-4 w-4",
                  i < stars
                    ? "fill-amber-400 text-amber-400"
                    : "fill-none text-gray-300"
                )}
              />
            ))}
          </div>
        ) : null}

        <h3 className="text-lg font-semibold text-gray-900 sm:text-xl">
          {name}
        </h3>

        {preview ? (
          <p className="text-sm leading-relaxed text-gray-600">{preview}</p>
        ) : (
          <p className="text-sm text-gray-400">No overview added yet.</p>
        )}

        <button
          type="button"
          onClick={onView}
          className="mt-auto flex w-full items-center justify-center gap-1 rounded-xl px-4 py-3 text-sm font-semibold text-white"
          style={quoteThemeColorStyles.primaryButton}
        >
          View
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>
    </article>
  );
}
